import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/AdminTPMSDefaultsPage.jsx
// Panel exclusivo de Arsenal para administrar TPMS Global Defaults.
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';

const CATEGORY_LABELS = {
    no_report: i18n.t('hardcoded.pages_admintpmsdefaultspage.falta_de_reporte'),
    dual_yoyo: i18n.t('hardcoded.components_alarmprofiles_profileform.dual'),
    general: 'General',
};

const CATEGORY_ORDER = ['no_report', 'dual_yoyo', 'general'];

const AdminTPMSDefaultsPage = () => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [edits, setEdits] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.admin.getTPMSDefaults();
            const list = Array.isArray(res.data) ? res.data : [];
            setItems(list);
            const initial = {};
            list.forEach((i) => { initial[i.key] = i.value; });
            setEdits(initial);
        } catch (err) {
            showError(err?.response?.data?.detail || t('hardcoded.pages_admintpmsdefaultspage.no_se_pudieron_cargar_los'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const grouped = useMemo(() => {
        const map = new Map();
        items.forEach((i) => {
            const cat = i.category || 'general';
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(i);
        });
        return CATEGORY_ORDER
            .filter((c) => map.has(c))
            .map((c) => [c, map.get(c)])
            .concat(Array.from(map.entries()).filter(([c]) => !CATEGORY_ORDER.includes(c)));
    }, [items]);

    const isDirty = useMemo(() => {
        return items.some((i) => String(edits[i.key]) !== String(i.value));
    }, [items, edits]);

    const handleChange = (key, raw, valueType) => {
        let next = raw;
        if (valueType === 'number' || valueType === 'percent') {
            next = raw === '' ? '' : Number(raw);
            if (Number.isNaN(next)) next = '';
        } else if (valueType === 'boolean') {
            next = Boolean(raw);
        }
        setEdits((prev) => ({ ...prev, [key]: next }));
    };

    const validate = (item, value) => {
        if (value === '' || value == null) return t('hardcoded.pages_admintpmsdefaultspage.el_valor_es_obligatorio');
        if (item.value_type === 'number' || item.value_type === 'percent') {
            if (Number(value) < 0) return t('hardcoded.pages_admintpmsdefaultspage.no_puede_ser_negativo');
            if (item.value_type === 'percent' && Number(value) > 100) return t('hardcoded.pages_admintpmsdefaultspage.no_puede_ser_mayor_a');
        }
        return null;
    };

    const handleSave = async () => {
        const errors = [];
        items.forEach((i) => {
            const err = validate(i, edits[i.key]);
            if (err) errors.push(`${i.label}: ${err}`);
        });
        if (errors.length) {
            showError(errors.join(' · '));
            return;
        }

        setSaving(true);
        try {
            const dirty = items.filter((i) => String(edits[i.key]) !== String(i.value));
            for (const item of dirty) {
                await api.admin.updateTPMSDefault(item.key, edits[item.key]);
            }
            showSuccess(t('hardcoded.pages_admintpmsdefaultspage.defaults_guardados_correctamente'));
            await load();
        } catch (err) {
            showError(err?.response?.data?.detail || t('hardcoded.pages_admintpmsdefaultspage.error_al_guardar'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3, pb: 6 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                        {i18n.t('hardcoded.pages_admintpmsdefaultspage.defaults_globales_tpms')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('hardcoded.pages_admintpmsdefaultspage.valores_aplicados_al_crear_nuevos')}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || saving}>
                        {i18n.t('hardcoded.components_operators_operatorvehiclespanel.recargar')}</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                    >
                        {i18n.t('settings.company.saveChanges')}</Button>
                </Stack>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2.5}>
                    {grouped.map(([category, rows]) => (
                        <Paper key={category} sx={{ p: 2.5 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                {CATEGORY_LABELS[category] || category}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                {rows.map((item) => {
                                    const editedValue = edits[item.key];
                                    const validationError = validate(item, editedValue);
                                    const changed = String(editedValue) !== String(item.value);
                                    return (
                                        <Grid key={item.key} item xs={12} sm={6} md={4}>
                                            <Card variant="outlined" sx={{ borderColor: changed ? 'warning.main' : 'divider' }}>
                                                <CardContent>
                                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                                        {item.label}
                                                    </Typography>
                                                    {item.description && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                                            {item.description}
                                                        </Typography>
                                                    )}
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type={item.value_type === 'string' ? 'text' : 'number'}
                                                        value={editedValue ?? ''}
                                                        onChange={(e) => handleChange(item.key, e.target.value, item.value_type)}
                                                        error={Boolean(validationError)}
                                                        helperText={validationError || (changed ? t('hardcoded.pages_admintpmsdefaultspage.modificado_sin_guardar') : t('hardcoded.pages_admintpmsdefaultspage.sin_cambios'))}
                                                        InputProps={item.unit ? {
                                                            endAdornment: <InputAdornment position="end">{item.unit}</InputAdornment>,
                                                        } : undefined}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Paper>
                    ))}
                    {!grouped.length && (
                        <Alert severity="info">{t('hardcoded.pages_admintpmsdefaultspage.no_hay_defaults_configurados')}</Alert>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default AdminTPMSDefaultsPage;
